import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';
import { authHeader } from './supabase';

// ─── CONSTANTES ───────────────────────────────────────────────
const STORAGE_AC = 'alqos_acciones_correctivas';
const STORAGE_NC = 'alqos_ncs';
const STORAGE_ANALISIS = 'alqos_analisis_causa';

const LOG = {
  info: (msg, data) => console.log(`[AC][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[AC][ERROR] ${msg}`, err || ''),
  warn: (msg, data) => console.warn(`[AC][WARN] ${msg}`, data || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};

// ─── CONSTANTES DE DOMINIO ────────────────────────────────────
const estadosAC = {
  abierta: { label: "🔴 Abierta", color: "#e63946" },
  en_proceso: { label: "🔵 En proceso", color: "#3b82f6" },
  implementada: { label: "🟡 Implementada", color: "#f59e0b" },
  verificada: { label: "🟣 Verificada", color: "#7B5BF2" },
  cerrada: { label: "✅ Cerrada", color: "#22c55e" },
};

const tiposAccion = {
  correccion: { label: "🔧 Corrección", color: "#f59e0b", desc: "Acción inmediata para eliminar el desvío" },
  correctiva: { label: "🔁 Correctiva", color: "#3b82f6", desc: "Elimina la causa raíz para evitar recurrencia" },
  preventiva: { label: "🛡️ Preventiva", color: "#22c55e", desc: "Evita que ocurra un problema potencial" },
};

const resultadoEficacia = {
  eficaz: { label: "✅ Eficaz", color: "#22c55e" },
  no_eficaz: { label: "🔴 No eficaz", color: "#e63946" },
  pendiente: { label: "⏳ Pendiente", color: "#808085" },
};

// ─── FUNCIONES DE LÓGICA ──────────────────────────────────────
function generarIdAC(lista) {
  const anio = new Date().getFullYear();
  const num = String(lista.length + 1).padStart(3, "0");
  return `AC-${anio}-${num}`;
}

function fechaHoy() { return new Date().toISOString().slice(0, 10); }

function obtenerNC(idNC) {
  try {
    const ncs = storage.get(STORAGE_NC);
    return ncs.find(n => n.nci?.toLowerCase() === idNC?.toLowerCase().trim()) || null;
  } catch (e) { return null; }
}

function obtenerAnalisisPorNC(idNC) {
  try {
    const analisis = JSON.parse(localStorage.getItem(STORAGE_ANALISIS) || '[]');
    return analisis.find(a => a.nciRelacionado === idNC) || null;
  } catch { return null; }
}

function obtenerACporNC(idNC) {
  try {
    const acs = storage.get(STORAGE_AC);
    return acs.filter(a => a.idNC === idNC);
  } catch (e) { return []; }
}

function exportarCSV(datos, nombre) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = Object.keys(datos[0]).filter(k => !k.startsWith('_'));
    const csv = [keys.join(","), ...datos.map(d => keys.map(k => `"${String(d[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${nombre}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    LOG.info("CSV exportado", { nombre, registros: datos.length });
  } catch (err) { LOG.error("Error exportar CSV", err); }
}

function exportarPDF(datos, titulo) {
  try {
    if (!datos.length) { alert("No hay datos."); return; }
    const keys = Object.keys(datos[0]).filter(k => !k.startsWith('_'));
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>body{font-family:Arial,sans-serif;font-size:11px;padding:20px}h1{color:#09184D;font-size:16px}
    table{width:100%;border-collapse:collapse;margin-top:16px}th{background:#09184D;color:white;padding:6px 8px;font-size:10px}
    td{padding:5px 8px;border-bottom:.5px solid #ddd;font-size:10px}tr:nth-child(even){background:#f7f7f7}</style></head>
    <body><h1>ALQOS™ — ${titulo}</h1>
    <p style="font-size:10px;color:#808085">Generado: ${new Date().toLocaleString('es-AR')} | Total: ${datos.length}</p>
    <table><thead><tr>${keys.map(k => `<th>${k}</th>`).join("")}</tr></thead>
    <tbody>${datos.map(d => `<tr>${keys.map(k => `<td>${d[k] || "—"}</td>`).join("")}</tr>`).join("")}</tbody></table>
    <p style="font-size:10px;color:#808085;margin-top:20px">Documento generado por ALQOS™ — Confidencial</p></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
  } catch (err) { LOG.error("Error exportar PDF", err); }
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function AccionesCorrectivas({ onVolver, oscuro, usuario }) {
  const [acciones, setAcciones] = useState([]);
const [cargandoAC, setCargandoAC] = useState(true);
useEffect(() => {
  async function cargar() {
    try {
      const { supabase } = await import('./supabase');
      const { data } = await supabase.from('acciones').select('data');
      if (data && data.length > 0) setAcciones(data.map(r => r.data));
      else setAcciones(storage.get(STORAGE_AC));
    } catch(e) { setAcciones(storage.get(STORAGE_AC)); }
    setCargandoAC(false);
  }
  cargar();
}, []);
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ tipoAccion: "correctiva", estado: "abierta", resultadoEficacia: "pendiente", fecha: fechaHoy() });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [errores, setErrores] = useState({});
  const [filtros, setFiltros] = useState({ estado: "todos", tipo: "todos" });
  const [busqueda, setBusqueda] = useState("");
  const [ncPreview, setNcPreview] = useState(null);
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [analisisPreview, setAnalisisPreview] = useState(null);

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  if (cargandoAC) return (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:bg }}>
    <p style={{ color: oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando acciones...</p>
  </div>
);

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  // ─── VALIDACIÓN ───────────────────────────────────────────
  const validar = () => {
    const e = {};
    if (!form.idNC?.trim()) e.idNC = "Requerido — vinculá una NC existente";
    else if (!obtenerNC(form.idNC.trim())) e.idNC = "La NC no existe en el sistema — verificá el código";
    if (!form.causaRaiz?.trim()) e.causaRaiz = "Requerido";
    if (!form.descripcionAccion?.trim()) e.descripcionAccion = "Requerido";
    if (!form.responsable?.trim()) e.responsable = "Requerido";
    if (!form.fechaCompromiso) e.fechaCompromiso = "Requerido";
    setErrores(e);
    LOG.info("Validación AC", { errores: Object.keys(e).length });
    return Object.keys(e).length === 0;
  };

  const validarCierre = (ac) => {
    if (!ac.evidenciaImplementacion?.trim()) { alert("No se puede cerrar sin evidencia de implementación."); return false; }
    if (!ac.evidenciaEficacia?.trim()) { alert("No se puede cerrar sin evidencia de eficacia."); return false; }
    if (!ac.resultadoEficacia || ac.resultadoEficacia === "pendiente") { alert("Definí el resultado de eficacia antes de cerrar."); return false; }
    return true;
  };

  // ─── GUARDADO ─────────────────────────────────────────────
  const guardar = () => {
    if (!validar()) return;
    try {
      const actuales = storage.get(STORAGE_AC);
      const nc = obtenerNC(form.idNC.trim());
      const nueva = {
        ...form,
        id: Date.now(),
        idAccion: generarIdAC(actuales),
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || "Sistema",
        rolCreador: usuario?.rol || "",
        estado: "abierta",
        // Datos de contexto de la NC
        procesoNC: nc?.proceso || "",
        clienteNC: nc?.cliente || "",
        descripcionNC: nc?.descripcion || "",
        _source: "localStorage",
        _schema: "ac_v1",
      };
      const nuevas = [...actuales, nueva];
      const ok = storage.set(STORAGE_AC, nuevas);
      subirDatos(STORAGE_AC);
      if (!ok) { alert("Error al guardar."); return; }

      LOG.info("Acción Correctiva guardada", { id: nueva.idAccion, nc: nueva.idNC });
      setAcciones(nuevas);
      setForm({ tipoAccion: "correctiva", estado: "abierta", resultadoEficacia: "pendiente", fecha: fechaHoy() });
      setErrores({}); setNcPreview(null);
      setVista("listado");
    } catch (err) {
      LOG.error("Error al guardar AC", err);
      alert("Error inesperado.");
    }
  };

  // ─── CAMBIO DE ESTADO ─────────────────────────────────────
  const cambiarEstado = (id, nuevoEstado) => {
    try {
      const ac = acciones.find(a => a.id === id);
      if (nuevoEstado === "cerrada" && !validarCierre(ac)) return;
      const acActual = acciones.find(a => a.id === id);
      const entradaHistorial = {
        de: acActual?.estado || "—",
        a: nuevoEstado,
        por: usuario?.nombre || "Sistema",
        rol: usuario?.rol || "",
        fecha: new Date().toISOString(),
      };
      const actualizadas = acciones.map(a => a.id === id ? {
        ...a,
        estado: nuevoEstado,
        historialEstados: [...(a.historialEstados || []), entradaHistorial],
        ...(nuevoEstado === "cerrada" ? { fechaCierre: new Date().toISOString(), cerradoPor: usuario?.nombre } : {}),
        ...(nuevoEstado === "verificada" ? { fechaVerificacion: new Date().toISOString() } : {}),
      } : a);
      setAcciones(actualizadas);
      storage.set(STORAGE_AC, actualizadas);
      subirDatos(STORAGE_AC);
      if (detalle?.id === id) setDetalle(prev => ({ ...prev, estado: nuevoEstado }));
      LOG.info("Estado AC cambiado", { id, nuevoEstado });
    } catch (err) { LOG.error("Error cambiar estado", err); }
  };

  // ─── ACTUALIZAR CAMPO ─────────────────────────────────────
  const actualizarCampo = (id, campo, valor) => {
    try {
      const actualizadas = acciones.map(a => a.id === id ? { ...a, [campo]: valor } : a);
      setAcciones(actualizadas);
      storage.set(STORAGE_AC, actualizadas);
      subirDatos(STORAGE_AC);
      if (detalle?.id === id) setDetalle(prev => ({ ...prev, [campo]: valor }));
    } catch (err) { LOG.error("Error actualizar campo", err); }
  };

  // ─── ELIMINAR ─────────────────────────────────────────────
  const eliminar = async (id) => {
    try {
      const nuevas = acciones.filter(a => a.id !== id);
      setAcciones(nuevas);
      storage.set(STORAGE_AC, nuevas);
      await eliminarDato(STORAGE_AC, id);
      LOG.info("AC eliminada", { id });
      setConfirmEliminar(null); setDetalle(null); setVista("listado");
    } catch (err) { LOG.error("Error eliminar", err); }
  };

  // ─── FILTROS ──────────────────────────────────────────────
  const accionesFiltradas = acciones.filter(a => {
    const matchEstado = filtros.estado === "todos" || a.estado === filtros.estado;
    const matchTipo = filtros.tipo === "todos" || a.tipoAccion === filtros.tipo;
    const matchBusqueda = !busqueda || a.idAccion?.toLowerCase().includes(busqueda.toLowerCase()) || a.idNC?.toLowerCase().includes(busqueda.toLowerCase()) || a.responsable?.toLowerCase().includes(busqueda.toLowerCase());
    return matchEstado && matchTipo && matchBusqueda;
  });

  const cerradas = acciones.filter(a => a.estado === "cerrada");
  const tiempoPromedio = cerradas.length > 0
    ? Math.round(cerradas.reduce((acc, a) => {
        if (!a.fechaCreacion || !a.fechaCierre) return acc;
        return acc + (new Date(a.fechaCierre) - new Date(a.fechaCreacion)) / (1000 * 60 * 60 * 24);
      }, 0) / cerradas.length)
    : null;
  const pctEficacia = cerradas.length > 0
    ? Math.round((cerradas.filter(a => a.resultadoEficacia === "eficaz").length / cerradas.length) * 100)
    : null;
  const resumen = {
    total: acciones.length,
    abiertas: acciones.filter(a => a.estado === "abierta").length,
    vencidas: acciones.filter(a => a.fechaCompromiso && new Date(a.fechaCompromiso) < new Date() && a.estado !== "cerrada").length,
    eficaces: acciones.filter(a => a.resultadoEficacia === "eficaz").length,
    pctEficacia,
    tiempoPromedio,
  };


  const sugerirConARIA = async () => {
    if (!form.idNC || !form.idNC.trim()) { alert("Primero vinculá una NC."); return; }
    const nc = obtenerNC(form.idNC.trim());
    if (!nc) { alert("NC no encontrada."); return; }
    setCargandoARIA(true);
    try {
      const analisis = obtenerAnalisisPorNC(form.idNC.trim());
      const prompt = "Sos experto en ISO 9001 acciones correctivas. Basándote en esta NC, sugerí una acción correctiva concreta. Respondé SOLO en JSON sin markdown.\n\nNC: " + nc.nci + "\nDescripción: " + nc.descripcion + "\nProceso: " + nc.proceso + "\nTipo: " + nc.tipoNC + "\nSeveridad: " + nc.severidad + "\nAcción inmediata: " + (nc.accionInmediata || "No registrada") + (analisis ? "\nCausa raíz (Análisis de Causa): " + analisis.causaRaizFinal : "") + "\n\nJSON exacto:\n{\n  \"causaRaiz\": \"causa raíz en una oración\",\n  \"accionCorrectiva\": \"descripción concreta\",\n  \"alcance\": \"alcance de la acción\",\n  \"diasSugeridos\": 15,\n  \"responsableSugerido\": \"rol recomendado\",\n  \"evidenciaSugerida\": \"qué evidencia recopilar\"\n}";
      const response = await fetch("/api/aria", {
        method: "POST",
        headers: { ...(await authHeader()),
          "Content-Type": "application/json",
          "anthropic-version": "2023-06-01",
          },
        body: JSON.stringify({ model: "claude-sonnet-4-5", max_tokens: 1000, messages: [{ role: "user", content: prompt }] })
      });
      const data = await response.json();
      const raw = data.content && data.content[0] ? data.content[0].text : "";
      const limpio = raw.replace(/```json/g,"").replace(/```/g,"").trim();
      const resultado = JSON.parse(limpio);
      const fechaSugerida = new Date();
      fechaSugerida.setDate(fechaSugerida.getDate() + (resultado.diasSugeridos || 15));
      const fechaStr = fechaSugerida.toISOString().slice(0, 10);
      setForm(f => ({
        ...f,
        causaRaiz: resultado.causaRaiz || f.causaRaiz,
        descripcionAccion: resultado.accionCorrectiva || f.descripcionAccion,
        alcance: resultado.alcance || f.alcance,
        fechaCompromiso: fechaStr,
        responsable: resultado.responsableSugerido || f.responsable,
        evidenciaPlanificada: resultado.evidenciaSugerida || f.evidenciaPlanificada,
      }));
    } catch(e) {
      console.error(e);
      alert("Error al conectar con ARIA.");
    }
    setCargandoARIA(false);
  };

  // ─── UI ───────────────────────────────────────────────────
  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔁</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar esta acción?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>Esta acción no se puede deshacer.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  // ─── DETALLE ──────────────────────────────────────────────
  if (vista === "detalle" && detalle) {
    const nc = obtenerNC(detalle.idNC);
    const accionesDeNC = obtenerACporNC(detalle.idNC);
    const vencida = detalle.fechaCompromiso && new Date(detalle.fechaCompromiso) < new Date() && detalle.estado !== "cerrada";

    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
        <Header titulo={detalle.idAccion}
          onBack={() => { setDetalle(null); setVista("listado"); }}
          extra={
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => exportarPDF([detalle], `AC_${detalle.idAccion}`)}
                style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px" }}>📄 PDF</button>
              {esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)}
                style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
            </div>
          }
        />
        <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>

          {vencida && (
            <div style={{ background: "#fff0f0", border: "2px solid #e63946", borderRadius: "12px", padding: "1rem", marginBottom: "12px", textAlign: "center" }}>
              <p style={{ fontSize: "14px", fontWeight: "700", color: "#e63946", margin: 0 }}>⏰ ACCIÓN VENCIDA — Fecha compromiso: {detalle.fechaCompromiso}</p>
            </div>
          )}

          {/* TRAZABILIDAD */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>🔗 Cadena de trazabilidad ISO 10.2</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {nc && (
                <div style={{ padding: "10px 12px", background: oscuro ? "#0d2160" : "white", borderRadius: "8px", border: "1px solid #e63946" }}>
                  <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>No Conformidad de origen</p>
                  <p style={{ fontSize: "13px", color: "#e63946", margin: "0 0 2px", fontWeight: "700" }}>{nc.nci}</p>
                  <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{nc.descripcion?.slice(0, 60) || "—"}</p>
                </div>
              )}
              <p style={{ textAlign: "center", fontSize: "16px", color: "#3b82f6", margin: 0 }}>↓</p>
              <div style={{ padding: "10px 12px", background: oscuro ? "#0d2160" : "white", borderRadius: "8px", border: "1px solid #7B5BF2" }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>Acción Correctiva</p>
                <p style={{ fontSize: "13px", color: "#7B5BF2", margin: "0 0 2px", fontWeight: "700" }}>{detalle.idAccion}</p>
                <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{tiposAccion[detalle.tipoAccion]?.label} · {detalle.responsable}</p>
              </div>
              <p style={{ textAlign: "center", fontSize: "16px", color: "#22c55e", margin: 0 }}>↓</p>
              <div style={{ padding: "10px 12px", background: detalle.resultadoEficacia === "eficaz" ? (oscuro ? "#0a2a1a" : "#f0fff4") : oscuro ? "#0d2160" : "#f8f9fa", borderRadius: "8px", border: `1px solid ${resultadoEficacia[detalle.resultadoEficacia]?.color || "#eee"}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>Verificación de eficacia</p>
                <p style={{ fontSize: "13px", fontWeight: "700", color: resultadoEficacia[detalle.resultadoEficacia]?.color, margin: 0 }}>{resultadoEficacia[detalle.resultadoEficacia]?.label}</p>
              </div>
            </div>
          </div>

          {/* IDENTIFICACIÓN */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "10px" }}>Identificación</h3>
            {[["ID Acción", detalle.idAccion], ["NC vinculada", detalle.idNC], ["Tipo de acción", tiposAccion[detalle.tipoAccion]?.label], ["Proceso NC", detalle.procesoNC], ["Fecha creación", detalle.fechaCreacion?.slice(0, 10)], ["Fecha compromiso", detalle.fechaCompromiso], ["Responsable", detalle.responsable], ["Creado por", `${detalle.creadoPor} — ${detalle.rolCreador}`]].map(([k, v]) => v && (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ color: subtexto }}>{k}</span>
                <span style={{ color: k === "NC vinculada" ? "#e63946" : texto, fontWeight: k === "NC vinculada" ? "700" : "500" }}>{v}</span>
              </div>
            ))}
          </div>

          {/* ANÁLISIS Y ACCIÓN */}
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#e63946", marginBottom: "12px" }}>Análisis y acción</h3>
            {[["Causa raíz", detalle.causaRaiz], ["Descripción de la acción", detalle.descripcionAccion], ["Alcance", detalle.alcance]].map(([k, v]) => v && (
              <div key={k} style={{ padding: "8px 0", borderBottom: `0.5px solid ${borde}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>{k}</p>
                <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{v}</p>
              </div>
            ))}
            {esAdmin && (
              <div style={{ marginTop: "12px" }}>
                <span style={lbl}>Causa raíz (editable)</span>
                <textarea rows={2} defaultValue={detalle.causaRaiz || ""} onBlur={e => actualizarCampo(detalle.id, "causaRaiz", e.target.value)} style={{ ...inp, resize: "none" }} />
                <span style={lbl}>Descripción de la acción (editable)</span>
                <textarea rows={2} defaultValue={detalle.descripcionAccion || ""} onBlur={e => actualizarCampo(detalle.id, "descripcionAccion", e.target.value)} style={{ ...inp, resize: "none" }} />
              </div>
            )}
          </div>

          {/* IMPLEMENTACIÓN Y EFICACIA */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0fff4", border: `1px solid ${oscuro ? "#22c55e44" : "#bbf7d0"}` }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#22c55e", marginBottom: "12px" }}>Implementación y eficacia</h3>
            {[["Evidencia implementación", detalle.evidenciaImplementacion], ["Evidencia eficacia", detalle.evidenciaEficacia], ["Fecha verificación", detalle.fechaVerificacion?.slice(0, 10)], ["Fecha cierre", detalle.fechaCierre?.slice(0, 10)], ["Cerrado por", detalle.cerradoPor]].map(([k, v]) => v && (
              <div key={k} style={{ padding: "6px 0", borderBottom: `0.5px solid ${borde}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>{k}</p>
                <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{v}</p>
              </div>
            ))}
            {esAdmin && detalle.estado !== "cerrada" && (
              <div style={{ marginTop: "12px" }}>
                <span style={lbl}>Evidencia de implementación (requerida para cerrar)</span>
                <textarea rows={2} defaultValue={detalle.evidenciaImplementacion || ""} onBlur={e => actualizarCampo(detalle.id, "evidenciaImplementacion", e.target.value)} style={{ ...inp, resize: "none" }} placeholder="Describí qué se hizo y cuándo..." />
                <span style={lbl}>Evidencia de eficacia (requerida para cerrar)</span>
                <textarea rows={2} defaultValue={detalle.evidenciaEficacia || ""} onBlur={e => actualizarCampo(detalle.id, "evidenciaEficacia", e.target.value)} style={{ ...inp, resize: "none" }} placeholder="Cómo verificaste que funcionó..." />
                <span style={lbl}>Resultado de eficacia</span>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  {Object.entries(resultadoEficacia).map(([val, { label, color }]) => (
                    <button key={val} onClick={() => actualizarCampo(detalle.id, "resultadoEficacia", val)}
                      style={{ flex: 1, padding: "8px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: detalle.resultadoEficacia === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.resultadoEficacia === val ? `${color}22` : cardBg, color: detalle.resultadoEficacia === val ? color : subtexto }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* FLUJO DE ESTADOS */}
          {esAdmin && (
            <div style={card}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "8px" }}>Flujo de estados ISO 10.2</h3>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {Object.entries(estadosAC).map(([val, { label, color }]) => (
                  <button key={val} onClick={() => cambiarEstado(detalle.id, val)}
                    style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>
                    {label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: "11px", color: subtexto, marginTop: "8px" }}>Flujo: Abierta → En proceso → Implementada → Verificada → Cerrada</p>
            </div>
          )}

          {detalle.historialEstados && detalle.historialEstados.length > 0 && (
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f8f9fa", border: `1px solid ${borde}` }}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "8px" }}>📋 Historial de estados</h3>
              {detalle.historialEstados.map((h, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `0.5px solid ${borde}`, fontSize: "12px" }}>
                  <span style={{ color: subtexto }}>{h.fecha ? h.fecha.slice(0, 16).replace("T", " ") : ""}</span>
                  <span style={{ color: texto }}><strong style={{ color: estadosAC[h.de] ? estadosAC[h.de].color : "" }}>{estadosAC[h.de] ? estadosAC[h.de].label : h.de}</strong> → <strong style={{ color: estadosAC[h.a] ? estadosAC[h.a].color : "" }}>{estadosAC[h.a] ? estadosAC[h.a].label : h.a}</strong></span>
                  <span style={{ color: subtexto }}>{h.por} · {h.rol}</span>
                </div>
              ))}
            </div>
          )}
          {/* OTRAS ACCIONES DE LA MISMA NC */}
          {accionesDeNC.length > 1 && (
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#3b82f6", marginBottom: "8px" }}>Otras acciones de la NC {detalle.idNC} ({accionesDeNC.length} total)</h3>
              {accionesDeNC.filter(a => a.id !== detalle.id).map(a => (
                <div key={a.id} style={{ padding: "6px 0", borderBottom: `0.5px solid ${borde}`, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12px", color: "#7B5BF2", fontWeight: "600" }}>{a.idAccion}</span>
                  <span style={{ fontSize: "11px", color: estadosAC[a.estado]?.color }}>{estadosAC[a.estado]?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── NUEVA ACCIÓN ─────────────────────────────────────────
  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva Acción Correctiva" onBack={() => { setVista("listado"); setErrores({}); setNcPreview(null); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>

        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>Trazabilidad automática</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>

        {/* VINCULACIÓN CON NC */}
        <div style={{ ...card, background: oscuro ? "#0a0a2a" : "#fff0f0", border: "1px solid #e6394644" }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#e63946", marginBottom: "8px" }}>⚠️ Vinculación con NC obligatoria</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 10px" }}>Cada acción correctiva debe estar vinculada a una No Conformidad registrada en el sistema.</p>
          <span style={lbl}>ID de No Conformidad (NCI) *</span>
          <input value={form.idNC || ""} onChange={e => {
            const val = e.target.value;
            setForm(f => ({ ...f, idNC: val }));
            const nc = obtenerNC(val.trim());
            setNcPreview(nc);
            const analisis = obtenerAnalisisPorNC(val.trim());
            setAnalisisPreview(analisis);
            if (analisis && analisis.causaRaizFinal) {
              setForm(f => ({ ...f, idNC: val, causaRaiz: analisis.causaRaizFinal }));
            }
          }} style={{ ...inp, borderColor: errores.idNC ? "#e63946" : borde }} placeholder="Ej: NCI-2026-001" />
          {errores.idNC && <p style={{ color: "#e63946", fontSize: "11px", margin: "4px 0 0" }}>{errores.idNC}</p>}
          {ncPreview && (
            <div style={{ marginTop: "8px", padding: "10px", background: "#22c55e22", border: "1px solid #22c55e", borderRadius: "8px" }}>
              <p style={{ fontSize: "12px", color: "#22c55e", fontWeight: "600", margin: "0 0 2px" }}>✅ NC encontrada</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px" }}>Proceso: {ncPreview.proceso} · Severidad: {ncPreview.severidad}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>{ncPreview.descripcion?.slice(0, 80) || "—"}</p>
            </div>
          )}
          {analisisPreview && (
            <div style={{ marginTop: "6px", padding: "10px", background: "#7B5BF222", border: "1px solid #7B5BF2", borderRadius: "8px" }}>
              <p style={{ fontSize: "12px", color: "#7B5BF2", fontWeight: "600", margin: "0 0 2px" }}>🔬 Análisis de Causa vinculado — causa raíz pre-cargada</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>{analisisPreview.causaRaizFinal?.slice(0, 80) || "Sin causa raíz definida"}</p>
            </div>
          )}
        </div>

        {/* TIPO DE ACCIÓN */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#09184D" }}>Tipo de acción</h3>
          {Object.entries(tiposAccion).map(([val, { label, color, desc }]) => (
            <button key={val} onClick={() => setForm(f => ({ ...f, tipoAccion: val }))}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", marginBottom: "6px", fontSize: "13px", cursor: "pointer", fontWeight: "600", textAlign: "left", border: form.tipoAccion === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.tipoAccion === val ? `${color}22` : cardBg, color: form.tipoAccion === val ? color : texto }}>
              {label}
              <span style={{ display: "block", fontSize: "11px", fontWeight: "400", color: form.tipoAccion === val ? color : subtexto, marginTop: "2px" }}>{desc}</span>
            </button>
          ))}
        </div>

        {/* ANÁLISIS */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#e63946" }}>Análisis y planificación</h3>
          <span style={lbl}>Causa raíz * {errores.causaRaiz && <span style={{ color: "#e63946" }}>— {errores.causaRaiz}</span>}</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, causaRaiz: e.target.value }))} style={{ ...inp, resize: "none", borderColor: errores.causaRaiz ? "#e63946" : borde }} placeholder="¿Por qué ocurrió la no conformidad?" />
          <span style={lbl}>Descripción de la acción * {errores.descripcionAccion && <span style={{ color: "#e63946" }}>— {errores.descripcionAccion}</span>}</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, descripcionAccion: e.target.value }))} style={{ ...inp, resize: "none", borderColor: errores.descripcionAccion ? "#e63946" : borde }} placeholder="¿Qué se va a hacer para eliminar la causa?" />
          <span style={lbl}>Alcance de la acción</span>
          <input onChange={e => setForm(f => ({ ...f, alcance: e.target.value }))} style={inp} placeholder="Ej: Afecta a toda la línea de impresión offset" />
        </div>

        {/* RESPONSABLE Y PLAZOS */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#22c55e" }}>Responsable y plazos</h3>
          <span style={lbl}>Responsable * {errores.responsable && <span style={{ color: "#e63946" }}>— {errores.responsable}</span>}</span>
          <input onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={{ ...inp, borderColor: errores.responsable ? "#e63946" : borde }} />
          <span style={lbl}>Fecha de creación</span>
          <input type="date" defaultValue={fechaHoy()} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={inp} />
          <span style={lbl}>Fecha compromiso de implementación * {errores.fechaCompromiso && <span style={{ color: "#e63946" }}>— {errores.fechaCompromiso}</span>}</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, fechaCompromiso: e.target.value }))} style={{ ...inp, borderColor: errores.fechaCompromiso ? "#e63946" : borde }} />
          <span style={lbl}>Evidencia planificada</span>
          <input onChange={e => setForm(f => ({ ...f, evidenciaPlanificada: e.target.value }))} style={inp} placeholder="Ej: Procedimiento actualizado, registro de capacitación" />
        </div>

        <button onClick={sugerirConARIA} disabled={cargandoARIA}
          style={{ width: "100%", padding: "12px", background: cargandoARIA ? "rgba(123,91,242,.3)" : "linear-gradient(135deg,#7B5BF2,#09184D)", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: cargandoARIA ? "default" : "pointer", marginTop: "1rem", marginBottom: "8px" }}>
          {cargandoARIA ? "🤖 Generando sugerencia..." : "🤖 Sugerir con ARIA"}
        </button>
        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "0" }}>
          Guardar Acción Correctiva
        </button>
      </div>
    </div>
  );

  // ─── LISTADO ──────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="accionescorrectivas" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="acciones" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Acciones Correctivas</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => exportarCSV(accionesFiltradas, "AccionesCorrectivas")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 Excel</button>
          <button onClick={() => exportarPDF(accionesFiltradas, "Acciones Correctivas ISO 10.2")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📄 PDF</button>
          <CampanaNotificaciones oscuro={oscuro} modulo="AccionesCorrectivas" />
          <button onClick={() => setVista("nueva")}
            style={{ padding: "6px 14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva</button>
        </div>
      </div>

      <div style={{ padding: "1rem 1.5rem" }}>
        {(() => {
          const proximas = acciones.filter(a => {
            if (a.estado === "cerrada") return false;
            const dias = a.fechaCompromiso ? Math.ceil((new Date(a.fechaCompromiso) - new Date()) / (1000*60*60*24)) : null;
            return dias !== null && dias >= 0 && dias <= 3;
          });
          if (proximas.length === 0) return null;
          return (
            <div style={{ background: "#fff8f0", border: "2px solid #f97316", borderRadius: "12px", padding: "12px 16px", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>⏰</span>
              <div>
                <p style={{ fontSize: "13px", fontWeight: "700", color: "#f97316", margin: 0 }}>{proximas.length} acción{proximas.length > 1 ? "es" : ""} vence{proximas.length === 1 ? "" : "n"} en los próximos 3 días</p>
                <p style={{ fontSize: "11px", color: "#808085", margin: 0 }}>{proximas.map(a => a.idAccion).join(" · ")}</p>
              </div>
            </div>
          );
        })()}
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total", resumen.total, "#7B5BF2"], ["Abiertas", resumen.abiertas, "#e63946"], ["Vencidas", resumen.vencidas, "#f97316"], ["Eficaces", resumen.eficaces, "#22c55e"], ["% Eficacia", resumen.pctEficacia !== null ? resumen.pctEficacia + "%" : "—", "#22c55e"], ["Días prom.", resumen.tiempoPromedio !== null ? resumen.tiempoPromedio + "d" : "—", "#3b82f6"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${lab === "Vencidas" && resumen.vencidas > 0 ? "#f97316" : borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>

        {/* FILTROS */}
        <input placeholder="🔍 Buscar por ID, NC o responsable..." value={busqueda} onChange={e => setBusqueda(e.target.value)} style={{ ...inp, marginBottom: "1rem", marginTop: 0 }} />
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "1rem" }}>
          <select value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}
            style={{ ...inp, marginTop: 0, flex: 1, minWidth: "120px", background: cardBg }}>
            <option value="todos">Todos los estados</option>
            {Object.entries(estadosAC).map(([val, { label }]) => <option key={val} value={val}>{label}</option>)}
          </select>
          <select value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))}
            style={{ ...inp, marginTop: 0, flex: 1, minWidth: "120px", background: cardBg }}>
            <option value="todos">Todos los tipos</option>
            {Object.entries(tiposAccion).map(([val, { label }]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </div>

        {/* LISTA */}
        {accionesFiltradas.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>🔁</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay acciones correctivas registradas</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Vinculá una NC y creá la primera acción</p>
            </div>
          : accionesFiltradas.map(a => {
              const vencida = a.fechaCompromiso && new Date(a.fechaCompromiso) < new Date() && a.estado !== "cerrada";
              return (
                <div key={a.id} onClick={() => { setDetalle(a); setVista("detalle"); }}
                  style={{ background: vencida ? (oscuro ? "#1a0a00" : "#fff8f0") : cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${vencida ? "#f97316" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(123,91,242,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ fontWeight: "700", color: "#7B5BF2", fontSize: "13px" }}>{a.idAccion}</span>
                      <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: "700", color: tiposAccion[a.tipoAccion]?.color, background: `${tiposAccion[a.tipoAccion]?.color}22`, padding: "2px 6px", borderRadius: "6px" }}>{tiposAccion[a.tipoAccion]?.label}</span>
                      <p style={{ fontSize: "12px", color: texto, marginTop: "4px", fontWeight: "600" }}>{a.descripcionAccion?.slice(0, 70) || "Sin descripción"}{a.descripcionAccion?.length > 70 ? "..." : ""}</p>
                      <p style={{ fontSize: "11px", color: subtexto, marginTop: "2px" }}>NC: {a.idNC} · Responsable: {a.responsable}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", color: estadosAC[a.estado]?.color }}>{estadosAC[a.estado]?.label}</span>
                      <span style={{ fontSize: "10px", fontWeight: "600", color: resultadoEficacia[a.resultadoEficacia]?.color }}>{resultadoEficacia[a.resultadoEficacia]?.label}</span>
                      {vencida && <span style={{ fontSize: "10px", color: "#f97316", fontWeight: "700" }}>⏰ VENCIDA</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#7B5BF2" : "#9b8cc4" }}>
                    Compromiso: {a.fechaCompromiso || "—"} · {a.fechaCreacion?.slice(0, 10)}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
